import type { VouchPreview } from "@/types/tina";

/**
 * What the low-score panel knows about the cascade.
 *
 * Three states, not two, and the distinction is the whole point. `"failed"` is **not**
 * the same as a preview reporting zero: an unknown blast radius still needs a
 * confirmation, where a known zero does not. And `"loading"` is not the same as
 * `"failed"` — collapsing them would make the Approve button change behaviour under the
 * curator's cursor the moment the preview lands, which is worse than the surprise this
 * disclosure exists to remove. While loading, Approve is simply disabled.
 */
export type VouchState =
  | { status: "loading" }
  | { status: "ready"; preview: VouchPreview }
  | { status: "failed" };

/** How many *other* events a vouch would release, or `null` when that is unknown. */
export function releasableCount(state: VouchState): number | null {
  if (state.status !== "ready" || !state.preview.applies) return null;
  return state.preview.releasable_count;
}

/** Approve stays disabled until we know what it would do. */
export function vouchPending(state: VouchState): boolean {
  return state.status === "loading";
}

/**
 * Does approving need a confirmation step?
 *
 * Yes when a cascade is known to be non-empty, and yes when the count could not be
 * loaded — an unknown blast radius is exactly when a curator should be asked. No when we
 * positively know no other event is waiting, so the common single-event approval keeps
 * its one-click flow and bulk curation does not slow down.
 */
export function needsVouchConfirm(state: VouchState): boolean {
  if (state.status === "failed") return true;
  const count = releasableCount(state);
  return count !== null && count > 0;
}

function venueLabel(preview: VouchPreview): string {
  return preview.entity_name
    ? `at ${preview.entity_name}`
    : `at this ${preview.entity ?? "venue"}`;
}

/**
 * The sentence the curator reads before approving.
 *
 * Approving a low-score event also **vouches for its venue**, and Tina then releases
 * every sibling parked behind that same venue penalty which the credit clears. That is
 * not obvious from a button labelled "Approve as-is", and before this the panel said it
 * "creates the event" — singular — while one click published 65.
 */
export function vouchSummary(state: VouchState): string {
  if (state.status === "failed") {
    return "Approving also vouches for this venue, which may release other events parked behind the same venue penalty. The number could not be loaded.";
  }
  if (state.status === "loading") {
    return "Checking what else is parked at this venue…";
  }
  const preview = state.preview;
  if (!preview.applies) {
    return "Approving creates this event. No other event is waiting on this venue.";
  }
  const count = preview.releasable_count;
  if (count === 0) {
    return `Approving also vouches for this venue. No other event is waiting ${venueLabel(preview)}.`;
  }
  const held =
    preview.held_count > 0
      ? ` ${preview.held_count} more stay${preview.held_count === 1 ? "s" : ""} parked — a vouch cannot fix those.`
      : "";
  return `Approving also releases ${count} other event${count === 1 ? "" : "s"} ${venueLabel(preview)}.${held}`;
}

/**
 * Amber banner disclosing the cascade, shown only when there is a known, non-empty one.
 *
 * Visually the sibling of `CoalescedResolveBanner`, and deliberately a **separate**
 * component: that one is about coalescing, where one resolution is applied to hidden
 * waiters. `low_score` does not coalesce — a low score is a property of one event's own
 * date, lineup and image. What is shared here is the *cause*, so every released event is
 * still re-scored on its own merits and only the venue penalty is removed.
 */
export function VouchReleaseBanner({ state }: { state: VouchState }) {
  const count = releasableCount(state);
  if (count === null || count <= 0) return null;
  return (
    <p
      role="status"
      className="rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-sm font-medium text-amber-800 dark:text-amber-200"
    >
      {vouchSummary(state)}
    </p>
  );
}
